// The cards: a train's (its line, destination, where it is, Follow, and its
// whole run: stations passed, the next one, the rest) and a station's (the
// next trains). One layout and one vocabulary for every city; a city says
// what the run is (CardHooks.tripRows) and may add a section to a station's
// card (London's National Rail board).
//
// A train card is a fixed frame: the header stays put and only the list of
// stations scrolls, which is the card's one scroller. It opens with the
// next station in the middle of the list.

import type { Arrival, Network, PageLine, StationId, TrainId, TrainMotion, TrainPrediction } from '@tol/core/types';
import { el, esc, lineVars, patchRows, setHtml, setLineColor } from './dom.ts';

// A station on a train's card.
export type RowKind = 'past' | 'next' | 'ahead' | 'via';
// Where a row's time comes from: the operator's live prediction, the page's
// own estimate (shown as ≈), the timetable, or nowhere.
export type TimeSource = 'predicted' | 'estimated' | 'scheduled' | 'none';
export interface TripRow {
  readonly sid: StationId;
  readonly kind: RowKind;
  // ms since epoch; 0 for none.
  readonly time: number;
  readonly source: TimeSource;
}

type TripListRow = { readonly type: 'day'; readonly label: string } | { readonly type: 'stop'; readonly row: TripRow };

// A short city trip needs no extra furniture. Once a run crosses a local
// date boundary, label every date so repeated clock times stay unambiguous.
function tripListRows(rows: readonly TripRow[], clockDate: (ms: number) => string): readonly TripListRow[] {
  const days = new Set(rows.flatMap((r) => (r.time > 0 ? [clockDate(r.time)] : [])));
  if (days.size < 2) return rows.map((row) => ({ type: 'stop', row }));
  const out: TripListRow[] = [];
  let shown = '';
  for (const row of rows) {
    const day = row.time > 0 ? clockDate(row.time) : '';
    if (day !== '' && day !== shown) {
      out.push({ type: 'day', label: day });
      shown = day;
    }
    out.push({ type: 'stop', row });
  }
  return out;
}

export interface StationSection {
  readonly title: string;
  // The section's rows (<li>s), rendered on every refresh.
  readonly rows: () => string;
  // Wires the section's list once, when the card opens.
  readonly wire?: (list: HTMLElement) => void;
}

// The read-only part of a tracked train that card renderers consume.
export interface CardTrain {
  readonly id: TrainId;
  readonly key: string;
  readonly pred: TrainPrediction;
  readonly motion: Readonly<TrainMotion>;
}

export interface CardHooks {
  // The whole run for a train's card, first station to last.
  readonly tripRows: (t: CardTrain, net: Network) => readonly TripRow[];
  // Text under the destination besides where the train is: its number, its
  // platform. Default: the prediction's platform.
  readonly trainNote?: (t: CardTrain) => string;
  // An extra section on a station's card, or null.
  readonly stationSection?: (sid: StationId) => StationSection | null;
}

export interface CardContext {
  readonly live: { readonly byStation: ReadonlyMap<StationId, readonly Arrival[]> };
  readonly net: () => Network | null;
  readonly lineStyle: (id: string) => PageLine;
  readonly hasLine: (id: string) => boolean;
  readonly hidden: () => ReadonlySet<string>;
  readonly now: () => number;
  readonly fmtEta: (ms: number) => string;
  readonly clockTime: (ms: number) => string;
  readonly clockDate: (ms: number) => string;
  readonly stationName: (sid: StationId) => string;
  readonly isFollowing: () => boolean;
  readonly setFollowing: (on: boolean) => void;
  readonly close: () => void;
}

export interface Cards {
  // Renders a train's card; full: build it (opening), else refresh it.
  readonly train: (card: HTMLElement, t: CardTrain, full: boolean) => void;
  readonly station: (card: HTMLElement, sid: StationId, full: boolean) => void;
  // Forgets which train's list was centered (on close).
  readonly reset: () => void;
}

// What a card says of a train or an arrival with no live data
// (TrainPrediction.scheduled): placed by the timetable.
export const SCHEDULED_NOTE = 'Scheduled — no live data';

// The header every card has: a swatch, a title, and the close button.
export function cardTop(title: string, extra = ''): string {
  return `<div class="handle" aria-hidden="true"></div>
    <div class="card-top"><span class="sw"></span><span class="grow">${title}</span>${extra}
    <button class="close" aria-label="Close">&times;</button></div>`;
}

export function createCards(hooks: CardHooks, ctx: CardContext): Cards {
  // The train whose list was centered when its card opened.
  let centeredFor: string | null = null;
  let cancelCenter: (() => void) | null = null;

  function rowHtml(r: TripRow): string {
    const name = esc(ctx.stationName(r.sid));
    if (r.kind === 'via') return `<li class="via"><span>${name}</span><span class="eta">passes</span></li>`;
    let when = '';
    let title = '';
    if (r.time > 0) {
      if (r.kind === 'past') when = ctx.clockTime(r.time);
      else if (r.source === 'predicted') when = ctx.fmtEta(r.time);
      else if (r.source === 'estimated') {
        when = `≈ ${ctx.fmtEta(r.time)}`;
        title = 'Estimated';
      } else if (r.source === 'scheduled') {
        when = ctx.clockTime(r.time);
        title = 'Scheduled';
      }
    }
    const cls = r.kind === 'ahead' ? '' : ` class="${r.kind}"`;
    return `<li${cls}><span>${name}</span><span class="eta"${title === '' ? '' : ` title="${title}"`}>${when}</span></li>`;
  }

  // Once per train, when its card opens: the list scrolled so the next stop
  // is in the middle. The sheet is still finding its size when the card
  // opens (fonts, the map's padding), so it's measured again over the first
  // moments and corrected while it's off.
  function centerNext(list: HTMLElement, id: string): void {
    if (centeredFor === id) return;
    centeredFor = id;
    cancelCenter?.();
    const scroller = list.parentElement;
    if (!scroller) return;
    let canceled = false;
    let scrollTop = scroller.scrollTop;
    const gestures = ['wheel', 'touchstart', 'pointerdown', 'keydown'];
    const cancel = (): void => {
      canceled = true;
      for (const event of gestures) scroller.removeEventListener(event, cancel);
    };
    cancelCenter = cancel;
    for (const event of gestures) scroller.addEventListener(event, cancel, { passive: true });
    const opened = performance.now();
    const settle = (): void => {
      if (canceled || centeredFor !== id || scroller.scrollTop !== scrollTop) {
        cancel();
        return;
      }
      const at = list.querySelector<HTMLElement>('.next');
      if (at) {
        const row = at.getBoundingClientRect();
        const box = scroller.getBoundingClientRect();
        const off = row.top + row.height / 2 - (box.top + box.height / 2);
        if (row.height > 0 && box.height > 0 && Math.abs(off) > 4) scroller.scrollTop += off;
        scrollTop = scroller.scrollTop;
      }
      if (performance.now() - opened < 800) requestAnimationFrame(settle);
      else cancel();
    };
    requestAnimationFrame(settle);
  }

  function train(card: HTMLElement, t: CardTrain, full: boolean): void {
    const p = t.pred;
    const pos = t.motion.pos;
    const l = ctx.lineStyle(p.lineId);
    const net = ctx.net();
    setLineColor(card, l.show);
    card.classList.add('trip-card');
    if (p.scheduled === true) card.classList.add('sched');
    else card.classList.remove('sched');
    const rows = net ? hooks.tripRows(t, net) : [];
    const where = pos
      ? pos.N === p.N && p.loc !== '' && !/^at platform$/iu.test(p.loc)
        ? p.loc
        : pos.f >= 0.999 || pos.P === null || pos.P === ''
          ? `At ${ctx.stationName(pos.N)}`
          : `Between ${ctx.stationName(pos.P)} and ${ctx.stationName(pos.N)}`
      : '';
    if (full) {
      // A rebuilt list is a new scroller: center it again.
      if (centeredFor === t.id) centeredFor = null;
      card.innerHTML = `
        ${cardTop(esc(l.label), '<button class="btn small" data-follow aria-pressed="false">Follow</button>')}
        <h3 data-dest></h3>
        <p class="sub" data-where></p>
        <div class="trip-scroll"><ol class="stops trip" data-stops></ol></div>`;
      el('.close', card).addEventListener('click', ctx.close);
      el('[data-follow]', card).addEventListener('click', () => {
        ctx.setFollowing(!ctx.isFollowing());
        train(card, t, false);
      });
    }
    // A train can change destination while its card is open.
    el('[data-dest]', card).textContent = p.dest === '' ? 'Train' : `To ${p.dest}`;
    // A scheduled train says so first; the city's note (its number) follows.
    const note = hooks.trainNote ? hooks.trainNote(t) : p.platform;
    el('[data-where]', card).textContent = [p.scheduled === true ? SCHEDULED_NOTE : '', where, note]
      .filter((s) => s !== '')
      .join('. ');
    // Refreshed every second while open: only rows whose time or state
    // changed are written (patchRows).
    const list = el('[data-stops]', card);
    const items = tripListRows(rows, ctx.clockDate).map((item) =>
      item.type === 'day'
        ? { key: `day ${item.label}`, html: `<li class="trip-day"><span>${esc(item.label)}</span></li>` }
        : { key: `stop ${item.row.sid}`, html: rowHtml(item.row) },
    );
    patchRows(
      list,
      items.length === 0 ? [{ key: 'none', html: '<li><span>No upcoming stops reported</span></li>' }] : items,
    );
    centerNext(list, t.id);
    const fb = el('[data-follow]', card);
    fb.setAttribute('aria-pressed', String(ctx.isFollowing()));
    fb.textContent = ctx.isFollowing() ? 'Following' : 'Follow';
  }

  function station(card: HTMLElement, sid: StationId, full: boolean): void {
    const st = ctx.net()?.stations.get(sid);
    if (!st) {
      ctx.close();
      return;
    }
    card.classList.remove('trip-card', 'sched');
    const lines = [...st.lines].filter((id) => ctx.hasLine(id));
    const first = lines[0];
    setLineColor(card, first === undefined ? '#EEF0F8' : ctx.lineStyle(first).show);
    const section = hooks.stationSection ? hooks.stationSection(sid) : null;
    // A section that wasn't there when the card opened (London's National
    // Rail board arrives after its station data) means a rebuild.
    if (!full && (section !== null) !== (card.querySelector('[data-section]') !== null)) {
      station(card, sid, true);
      return;
    }
    // With a second section, fewer of each, so both fit on a phone.
    const arr = (ctx.live.byStation.get(sid) ?? [])
      .filter((a) => a.eta > ctx.now() - 20000 && !ctx.hidden().has(a.lineId))
      .slice(0, section ? 4 : 8);
    // Keyed by vehicle where there is one, else by line, destination and
    // platform (patchRows counts repeats), so a departure leaving the board
    // removes its row and leaves the others' nodes.
    const items = arr.map((a) => {
      const l = ctx.lineStyle(a.lineId);
      // A scheduled departure (no live data) is marked as one.
      const sched = a.scheduled === true;
      const plat = [l.label, a.platform, sched ? 'scheduled' : ''].filter((s) => s !== '').join(', ');
      return {
        key: a.vid === '' ? `${a.lineId} ${a.dest} ${a.platform}` : `${a.lineId} ${a.vid}`,
        html: `<li style="${lineVars(l.show)}"${sched ? ' class="sched"' : ''}><span class="bar"></span><span>${esc(a.dest === '' ? l.label : a.dest)}</span><span class="eta"${sched ? ` title="${SCHEDULED_NOTE}"` : ''}>${ctx.fmtEta(a.eta)}</span>
          <span class="plat">${esc(plat)}</span></li>`,
      };
    });
    if (full) {
      card.innerHTML = `
        ${cardTop(esc(lines.map((id) => ctx.lineStyle(id).label).join(', ')))}
        <h3>${esc(st.name)}</h3>
        <ul class="arrivals" data-arr></ul>
        ${section ? `<h4 class="card-sub">${esc(section.title)}</h4><ul class="arrivals" data-section></ul>` : ''}`;
      el('.close', card).addEventListener('click', ctx.close);
      if (section?.wire) section.wire(el('[data-section]', card));
    }
    patchRows(
      el('[data-arr]', card),
      items.length === 0
        ? [
            {
              key: 'none',
              html: `<li><span></span><span class="muted">No ${lines.length > 0 && lines.every((id) => ctx.lineStyle(id).minor === true) ? 'buses' : 'trains'} predicted right now</span></li>`,
            },
          ]
        : items,
    );
    if (section) setHtml(el('[data-section]', card), section.rows());
  }

  return {
    train,
    station,
    reset: () => {
      cancelCenter?.();
      centeredFor = null;
    },
  };
}
